/**
 * Clinical Trials ACF Block.
 *
 * Description: Displays results for Clincial Trials block asynchronously.
 */
import $ from 'jquery';
import 'whatwg-fetch';

class ResourcePublications {
	constructor() {
		this.perPage = 10;
		this.currentPage = 1;
		this.totalPages = 0;
		this.totalPosts = 0;
		this.window = window;
		this.hasTreatment = true;
		this.hasCondition = true;
		this.restUrl = '';
		this.nonce = '';
		this.endpoint = '';
		this.posts = {};
		this.postId = '';
		this.terms = [];
		this.location = window.location;
		this.initialQueryString = '';
		this.searchQuery = '';
		this.conditionQuery = '';
		this.treatmentQuery = '';
		this.filterKey = '_resource-filter';
		this.filter = '';
		this.filterDefault = '&_embed=1&order=desc&orderby=date';
		this.filterResetClass = 'em-resource-publications__filter-reset';
		this.columnInfo = {};
		this.storageIds = ['_resource-treatment', '_resource-condition'];
		this.isInitialLoad = true;

		this.container = document.querySelector('.em-resource-publications');
		if (this.container) {
			this.resultsContainer = this.container.querySelector('.em-resource-publications__results');
			this.paginationContainer = this.container.querySelector('.em-resource-publications__pagination');
			this.treatmentsSelect = this.container.querySelector('.em-resource-publications__field select[name="treatments"]');
			this.conditionsSelect = this.container.querySelector('.em-resource-publications__field select[name="conditions"]');
			this.searchBar = this.container.querySelector('.em-resource-publications__search-bar');
			this.searchForm = this.container.querySelector('.em-resource-publications__search');
			this.searchInput = this.container.querySelector('.search-field');
			this.breadcrumbs = document.querySelector('.breadcrumbs');
			this.resourceTypes = JSON.parse(this.searchBar.dataset.resourceType);
		}
	}

	/**
     * Gets local storage items.
     * @param {string} item Key data is stored under.
     */
	storageGet(item) {
		const value = this.window.localStorage.getItem(item);
		return JSON.parse(value);
	}

	/**
     * Sets local storage items.
     * @param {string} item Key to store data under.
     * @param {string} data Data to Store.
     */
	storageSet(item, data) {
		this.window.localStorage.setItem(item, JSON.stringify(data));
	}

	/**
     * Clears all local storage items for on page load event.
	 * @param {array} keyArray Storage key to clear.
     */
	storageClear(keyArray = []) {
		const values = keyArray.length ? keyArray : this.storageIds;
		values.forEach((value) => {
			this.window.localStorage.removeItem(value);
		});
	}

	/**
	 * Sets storage key asynchronously to get around caching.
	 * @param {string} key Key Value to set.
	 */
	async storageSetInitalData(key) {
		await null;
		return this.storageSet(key, '');
	}

	/**
	 * Sets category data into local storage for setting later.
	 */
	setCategoryStorage() {
		const { terms, postId } = this;

		if (terms.length) {
			const termData = [].concat(...terms); // Flatten array.
			termData.forEach((term) => {
				const type = term.taxonomy.replace('em-', '');
				const storageName = `_resource-${type}`;
				const data = {
					postId,
					name: term.name,
					taxonomy: term.taxonomy,
				};
				const storage = this.storageGet(storageName);
				const updatedStorage = storage !== null ? [...storage, data] : [data];
				this.storageSet(storageName, updatedStorage);
			});
		}
		return true;
	}

	/**
	 * * Set term DOM data from WordPress APi.
	 */
	setCategoryTerms() {
		const { postId, name, type } = this.columnInfo;
		const categoryType = type.replace('_resource-', '');
		const column = document.querySelector(`[data-${categoryType}="${postId}"]`);

		if (column !== null) {
			const span = document.createElement('span');
			span.innerHTML = name;
			column.appendChild(span);
			column.setAttribute('data-hasitems', 'true');
		}
		return true;
	}

	/**
	 * Returns Search Input Value.
	 */
	getSearchValue() {
		const { searchForm } = this;
		return searchForm.querySelector('input[type="text"]').value;
	}

	/**
	 * Returns Select Value of Center or Condition fields.
	 * @param {string} type center or condition.
	 */
	getSelect(type) {
		const { treatmentsSelect, conditionsSelect } = this;
		let selectField = '';
		let fieldValue = '';
		let visable = true;

		switch (type) {
			case 'treatment':
				selectField = treatmentsSelect;
				break;
			case 'condition':
				selectField = conditionsSelect;
				break;
			default:
				break;
		}

		if (selectField === '') {
			return {};
		}

		if (selectField !== null) {
			fieldValue = selectField.options[selectField.selectedIndex].value;
		} else {
			fieldValue = type === 'condition' ? this.searchBar.dataset.condition : this.searchBar.dataset.treatment;
			visable = false;
		}

		return {
			value: fieldValue,
			visable,
			queryString: type === 'condition' ? 'em-condition' : 'em-treatment',
		};
	}

	/**
	 * Sets Select Values for Filters.
	 */
	setSelectValues() {
		const {
			treatmentsSelect, conditionsSelect, totalPages, container,
		} = this;

		/**
		 * Limit filtering select values to single page of results only. We
		 * don't know what's on the other pages.
		 */
		if (totalPages > 1) {
			container.setAttribute('data-multipage', 'true');
		} else {
			container.setAttribute('data-multipage', 'false');
		}

		if (container.dataset.multipage === 'true') {
			return false;
		}

		const selectObject = {
			treatment: treatmentsSelect,
			condition: conditionsSelect,
		};

		Object.entries(selectObject).forEach((value) => {
			if (value[1] === null) {
				return;
			}

			const title = value[0];
			const nodes = document.querySelectorAll(`[data-${title}] span`);
			const itemArray = [];

			/**
			 * Grab Existing Values within DOM.
			 */
			Object.keys(nodes).forEach((key) => {
				const value = nodes[key];
				if (itemArray.indexOf(value.innerText) === -1) {
					itemArray.push(value.innerText);
				}
			});
		});

		return true;
	}

	/**
	 * Retrieves query string from address bar.
	 */
	getQueryString() {
		const { href } = this.location;
		let queryString = href.split('?')[1];
		queryString = typeof queryString !== 'undefined' ? queryString : '';
		return queryString;
	}

	/**
	 * Sets query string to the url via pushState.
	 */
	setQueryString() {
		const { href } = this.location;
		const {
			currentPage, searchQuery, treatmentQuery, conditionQuery,
		} = this;
		const currentPageQuery = currentPage > 1 ? `current_page=${currentPage}` : '';
		const search = searchQuery.length ? `&search=${searchQuery}` : '';
		const treatment = (treatmentQuery.length && treatmentQuery !== '0') ? `&treatment=${treatmentQuery}` : '';
		const condition = (conditionQuery.length && conditionQuery !== '0') ? `&condition=${conditionQuery}` : '';

		/** Setting Query string with accounting for no current_page paramenter if on the first page */
		let queryString = `${currentPageQuery}${search}${treatment}${condition}`;
		if (queryString.charAt(0) === '&') {
			queryString = queryString.substring(1);
		}
		queryString = queryString.length ? `?${queryString}` : '';

		let path = href.split('?')[0];
		path = typeof path !== 'undefined' ? `${path}${queryString}` : `${href}${queryString}`;

		window.history.pushState({}, '', path);
		return true;
	}

	/**
	 * Searchings Querystring for parameter and outputs value.
	 * @param {string} name Name of Parameter to search.
	 * @param {string} url  Url or string to search.
	 * @param {string} type Type for url or string.
	 */
	getQueryStringParam(name, url, type = 'url') {
		const { href } = this.location;
		if (!url) url = href;
		let regex = '';
		switch (type) {
			case 'url':
				regex = new RegExp(`[?&]${name}(=([^&#]*)|&|#|$)`);
				break;
			case 'string':
				regex = new RegExp(`${name}(=([^&#]*)|&|#|$)`);
				break;
			default:
				break;
		}
		const results = regex.exec(url);
		if (!results) return null;
		if (!results[2]) return '';
		return decodeURIComponent(results[2].replace(/\+/g, ' '));
	}

	/**
	 * Primary Resource Publications DOM output method.
	 */
	renderContent() {
		const { posts } = this;
		let html = '';

		/**
		 * Clear storage before outputting new content.
		 */
		this.storageClear();

		posts.forEach((post) => {
			const {
				id, title, resourceData,
			} = post;
			const {
				hasTreatment, hasCondition,
			} = this;
			const excerpt = resourceData.excerpt ? `<p>${resourceData.excerpt}</p>` : '';
			const linkUrl = resourceData.link_url;
			const target = resourceData.target ? `target="${resourceData.target}"` : 'target="_self"';
			const className = target === 'target="_blank"' ? 'class="external-link"' : '';
			const postTitle = linkUrl ? `<h2><a href="${linkUrl}" ${target} ${className}>${title.rendered}</a></h2>` : `<h2>${title.rendered}</h2>`;
			const dataContainer = `data-condition="${id}"`;
			const dataTreatment = `data-treatment="${id}"`;
			const featuredImg = (this.resourceTypes[0] === "322" && post.resource_img) ? `<img src="${post.resource_img[0]}" alt="${post.resource_img[1]}" class="featured-image" />` : '';

			/**
			 * Set Column Display
			 */
			const conditionColumn = hasCondition ? `<div class="em-resource-publications__column" ${dataContainer}></div>` : '';
			const treatmentColumn = hasTreatment ? `<div class="em-resource-publications__column" ${dataTreatment}></div>` : '';

			html += `<div class="em-resource-publications__item">
						${featuredImg}
					<div class="em-resource-publications__column">
						${postTitle}
						${excerpt}
						</div>
					${conditionColumn}
					${treatmentColumn}
				</div>`;

			/**
			 * Set Categories in respective columns.
			 */
			const embedded = post._embedded; // es-link ignore line;
			let terms = typeof embedded !== 'undefined' ? embedded['wp:term'] : '';
			terms = typeof terms !== 'undefined' ? terms : '';
			this.terms = terms;
			this.postId = id;
			this.setCategoryStorage();
		});

		/**
		 * Hide and replace old DOM content.
		 */
		this.resultsContainer.setAttribute('data-loaded', 'true');
		this.resultsContainer.style.opacity = 0;
		this.resultsContainer.innerHTML = html;

		/**
		 * Set Categories in respective columns.
		 */
		this.storageIds.forEach((id) => {
			const dataItems = this.storageGet(id);
			if (dataItems !== null) {
				dataItems.forEach((item) => {
					this.columnInfo = {
						postId: item.postId,
						name: item.name,
						type: id,
					};
					this.setCategoryTerms();
				});
			}
		});

		this.setSelectValues();

		/**
		 * Display results.
		 */
		$(this.resultsContainer).animate({ opacity: 1 }, 550);

		return true;
	}

	/**
	 * Renders out pagination based on custom WordPress API endpoint.
	 */
	async renderPagination() {
		const {
			totalPages, currentPage,
		} = this;

		if (parseInt(totalPages) === 1) {
			this.paginationContainer.innerHTML = '';
			return false;
		}

		const url = `${this.restUrl}em-client/v1/pagination`;
		try {
			const response = await fetch(url, {
				method: 'POST',
				body: JSON.stringify({
					total_pages: totalPages,
					current: currentPage,
				}),
				headers: {
					'Content-Type': 'application/json',
					'X-WP-Nonce': this.nonce,
				},
			});

			const json = await response.json();

			if (json.html) {
				this.paginationContainer.innerHTML = json.html;
				this.paginationClickHandler();
			}

			return true;
		} catch (error) {
			console.error('Error: ', error);
			return false;
		}
	}

	/**
	 * Click Event handler for pagination to asynchronously call new content.
	 */
	paginationClickHandler() {
		const paginationLinks = this.paginationContainer.querySelectorAll('.pagination a');

		paginationLinks.forEach((link) => {
			link.addEventListener('click', (e) => {
				e.preventDefault();
				const { target } = e;
				let newCurrentPage = null;

				if (target.tagName === 'SPAN') {
					const parent = e.target.closest('a');
					newCurrentPage = parent.href.split('/').pop();
				} else {
					newCurrentPage = target.href.split('/').pop();
				}

				if (newCurrentPage !== null) {
					this.initialQueryString = this.getQueryString();
					this.currentPage = newCurrentPage;

					$('html, body').animate({
						scrollTop: $(this.container).offset().top - 250,
					}, 500);
					this.fetchPostData(false, newCurrentPage);
				}
			});
		});
	}

	/**
	 * Outputs updated breadcrumbs based on current page.
	 */
	renderBreadCrumbs() {
		const { currentPage } = this;
		if (currentPage.toString() === '1') {
			const lastContainer = document.querySelector('.breadcrumb_last_container');
			if (lastContainer !== null) {
				/** Reset breadcrumbs back to the way we found it. */
				const defaultLastBreadCrumb = document.querySelector('.default_breadcrumb_last');
				lastContainer.parentNode.removeChild(lastContainer);
				defaultLastBreadCrumb.style.display = 'inline';
				defaultLastBreadCrumb.setAttribute('aria-hidden', 'false');
				defaultLastBreadCrumb.removeAttribute('aria-hidden');
				defaultLastBreadCrumb.setAttribute('aria-current', 'page');
			}
		} else {
			this.createBreadCrumbElement();
		}

		return true;
	}

	/**
	 * Creates the bits needed for a new breadcrumb page crumb.
	 */
	createBreadCrumbElement() {
		const { breadcrumbs, currentPage } = this;
		const imposter = document.querySelector('.breadcrumb_imposter');

		if (imposter === null) {
			/**
			 * Store default breadcrumb_last information.
			 */
			const lastBreadCrumb = breadcrumbs.querySelector('.breadcrumb_last');
			lastBreadCrumb.classList.add('default_breadcrumb_last');
			const lastBreadCrumbText = lastBreadCrumb.innerText;

			/** Create new arrow for breadcrumb */
			const angleRight = document.createElement('i');
			// classList.add parameters seperated for IE11 support
			angleRight.classList.add('fa');
			angleRight.classList.add('fa-angle-right');

			/** Create new breadCrumbLast element */
			const newBreadCrumbLast = document.createElement('span');
			// classList.add parameters seperated for IE11 support
			newBreadCrumbLast.classList.add('breadcrumb_last');
			newBreadCrumbLast.classList.add('breadcrumb_imposter');
			newBreadCrumbLast.setAttribute('aria-current', 'page');
			newBreadCrumbLast.innerText = `Page ${currentPage}`;

			/** Create link version of current lastBreadCrumb */
			const currentBreadCrumbLink = document.createElement('a');
			currentBreadCrumbLink.innerText = lastBreadCrumbText;
			this.setBreadCrumbLink(currentBreadCrumbLink);

			/** Create new breadCrumbLast Container */
			const newContainer = document.createElement('span');
			newContainer.classList.add('breadcrumb_last_container');
			lastBreadCrumb.parentElement.appendChild(newContainer);

			/** Build Breadcrumb content */
			lastBreadCrumb.style.display = 'none';
			lastBreadCrumb.removeAttribute('aria-current');
			lastBreadCrumb.setAttribute('aria-hidden', 'true');
			const breadCrumbLastContainer = document.querySelector('.breadcrumb_last_container');
			breadCrumbLastContainer.appendChild(currentBreadCrumbLink);
			breadCrumbLastContainer.appendChild(angleRight);
			breadCrumbLastContainer.appendChild(newBreadCrumbLast);
		} else {
			/** Update breadcrumb and link based on filters and currentPage */
			imposter.innerText = `Page ${currentPage}`;
			const linkElement = imposter.parentElement.querySelector('a');
			this.setBreadCrumbLink(linkElement);
		}
		return true;
	}

	/**
	 * Persists query string filters for created default breadcrumb link, sans current_page.
	 * @param {object} element Element to set link on.
	 */
	setBreadCrumbLink(element) {
		const {
			location, treatmentQuery, conditionQuery, searchQuery,
		} = this;

		/** We only want to allow this functionality for A html tags */
		if (element.tagName === 'A') {
			/** Gather website url removing existing query parementers */
			let url = location.href.split('?')[0];
			url = typeof url !== 'undefined' ? url : location.href;

			/** Get and set filter values from existing query parameters */
			const search = searchQuery.length ? `search=${searchQuery}` : '';
			const treatment = (treatmentQuery.length && treatmentQuery !== '0') ? `&treatment=${treatmentQuery}` : '';
			const condition = (conditionQuery.length && conditionQuery !== '0') ? `&condition=${conditionQuery}` : '';

			/** Attach querystring to url of element. */
			let queryString = `${search}${treatment}${condition}`;
			if (queryString.charAt(0) === '&') {
				queryString = queryString.substring(1);
			}
			queryString = queryString.length ? `?${queryString}` : '';

			/** Set the url and add it to the element */
			url = `${url}${queryString}`;
			element.setAttribute('href', url);

			return true;
		}
		return false;
	}

	/**
	 * Clears Clinical Trials filters.
	 */
	clearFilters() {
		const {
			searchForm, treatmentsSelect, conditionsSelect,
		} = this;
		const button = document.querySelector(`.${this.filterResetClass} button`);
		button.addEventListener('click', () => {
			/**
			 * Clear Input fields.
			 */
			searchForm.querySelector('input[type="text"]').value = '';
			if (treatmentsSelect !== null) {
				treatmentsSelect.selectedIndex = 0;
			}
			if (conditionsSelect !== null) {
				conditionsSelect.selectedIndex = 0;
			}

			this.fetchPostData(true);
		});
	}

	/**
	 * Fetches all values from filter fields.
	 */
	filterEndpoint() {
		const filterValues = {};
		/**
		 * Check Treatment and Condition Value based on settings.
		 */
		const treatmentSelect = this.getSelect('treatment');
		const conditionSelect = this.getSelect('condition');
		filterValues.treatment = treatmentSelect.value ? parseInt(treatmentSelect.value.replace('"', ''), 10) : false;
		filterValues.condition = conditionSelect.value ? parseInt(conditionSelect.value.replace('"', ''), 10) : false;

		/**
		 * Set Search Value.
		 */
		filterValues.search = this.getSearchValue();

		/** Set/Override Values for filters based on Query string parameters */
		if (this.initialQueryString !== '') {
			filterValues.treatment = this.treatmentQuery !== null ? this.treatmentQuery : filterValues.treatment;
			filterValues.condition = this.conditionQuery !== null ? this.conditionQuery : filterValues.condition;
			filterValues.search = this.searchQuery !== null ? this.searchQuery : filterValues.search;

			/** Set display of filters based on the query string */
			this.setFiltersBasedOnQueryString();
		}

		/** Set Query string parameters */
		this.treatmentQuery = filterValues.treatment !== null ? filterValues.treatment.toString() : '';
		this.conditionQuery = filterValues.condition !== null ? filterValues.condition.toString() : '';
		this.searchQuery = filterValues.search !== '' ? filterValues.search : '';


		/**
		 * Loop out object to add filters for query string.
		 */
		let filters = '';
		Object.entries(filterValues).forEach((value) => {
			const _key = value[0];
			const _value = value[1];

			if (_key === 'search' && _value) {
				filters += `&search=${encodeURI(_value)}`;
			} else if (_value) {
				filters += `&em-${_key}=${encodeURI(_value)}`;
			}
		});

		/**
		 * Filters out the query string segment build above, then checked against filter
		 * fields and remove any that may have ended up as a default (data-attribute) set
		 * by ACF field settings.
		 */
		const checkArray = [];
		const filtersCheckable = filters;
		if (!treatmentSelect.visable) {
			checkArray.push(treatmentSelect.queryString);
		}
		if (!conditionSelect.visable) {
			checkArray.push(conditionSelect.queryString);
		}

		let filteredQueryString = filtersCheckable.split('&');
		filteredQueryString = filteredQueryString.filter(Boolean);
		filteredQueryString.forEach((part, key) => {
			const newPart = part.split('=')[0];
			if (checkArray.indexOf(newPart) > -1) {
				delete filteredQueryString[key];
			}
		});

		/** Clean array of empty values */
		filteredQueryString = filteredQueryString.filter(Boolean);

		/**
		 * Add Clear filter button.
		 */
		const filterResetButton = document.querySelector(`.${this.filterResetClass}`);
		if (filterResetButton === null && filteredQueryString.length) {
			const button = document.createElement('button');
			button.innerHTML = 'Clear All Filters';

			const buttonContainer = document.createElement('div');
			buttonContainer.className = this.filterResetClass;
			buttonContainer.appendChild(button);

			this.searchBar.parentNode.insertBefore(buttonContainer, this.searchBar.nextElementSibling);

			/**
			 * Enable Filter Clearing.
			 */
			this.clearFilters();
		} else if (filterResetButton !== null && filteredQueryString.length === 0) {
			filterResetButton.parentNode.removeChild(filterResetButton);
		}

		/**
		 * Adds default filters.
		 */
		filters += this.filterDefault;
		this.storageSet(this.filterKey, filters);

		return true;
	}

	/**
	 * Event Methods for setting filters and refreshing results.
	 */
	resultsFilter() {
		const {
			searchForm, treatmentsSelect, conditionsSelect, searchInput,
		} = this;

		if (searchForm) {
			searchForm.addEventListener('submit', (e) => {
				e.preventDefault();
				// Remove the .invalid class from the search input if the form is valid.
				if (searchInput) {
					searchInput.classList.remove('invalid');
				}

				this.fetchPostData(true);
			});
		}

		if (searchInput) {
			// Add class to search input if it is invalid so it can be styled accordingly.
			searchInput.addEventListener('invalid', () => {
				searchInput.classList.add('invalid');
			});
		}

		if (treatmentsSelect || conditionsSelect) {
			const selects = [
				treatmentsSelect, conditionsSelect,
			];

			/**
			 * Loop through Select Objects
			 */
			selects.forEach((select) => {
				if (select) {
					select.addEventListener('change', () => {
						this.fetchPostData(true);
					});
				}
			});
		}
	}

	/**
	 * Sets default filter field values based on inital load of page and filters present.
	 */
	setFiltersBasedOnQueryString() {
		const {
			treatmentsSelect, conditionsSelect, searchInput, treatmentQuery,
			conditionQuery, searchQuery,
		} = this;

		/** Set Default Search input if exists */
		if (searchInput !== null && searchQuery !== '') {
			searchInput.value = searchQuery;
		}

		/** Set Default Center select value if exists */
		if (treatmentsSelect !== null && treatmentQuery !== '') {
			treatmentsSelect.value = treatmentQuery;
		}

		/** Set Default Condition select value if exists */
		if (conditionsSelect !== null && conditionQuery !== '') {
			conditionsSelect.value = conditionQuery;
		}
	}

	/**
	 * Primary method for clincial data posts.
	 * @param {bool} resetCurrentPage Optional Parameter to set the current page to 1.
	 * * @param {mixed} setCurrentPage  Allows for the manual setting of the current page.
	 */
	async fetchPostData(resetCurrentPage = false, setCurrentPage = null) {
		/**
		 * Set Current page to 1 if true.
		 */
		if (resetCurrentPage) {
			this.currentPage = 1;
		}

		/** Sets up query string with current querystring data */
		if (this.initialQueryString !== '') {
			/** Gather new current page from querystring */
			const newCurrentPage = this.getQueryStringParam('current_page');
			this.currentPage = newCurrentPage !== null ? newCurrentPage.toString() : this.currentPage;

			const search = this.getQueryStringParam('search');
			this.searchQuery = search !== null ? search : '';

			const treatment = this.getQueryStringParam('treatment');
			this.treatmentQuery = treatment !== null ? treatment : '';

			const condition = this.getQueryStringParam('condition');
			this.conditionQuery = condition !== null ? condition : '';
		}

		/**
		 * Overrides any setting of the current page, for edge cases.
		 * Specifially pagination page selection.
		 * */
		if (setCurrentPage !== null) {
			this.currentPage = setCurrentPage;
		}

		this.filterEndpoint();
		this.renderBreadCrumbs(); // Where we actually add onto the breadcrumbs.
		const filters = this.storageGet(this.filterKey);

		/** Set QueryString for API */
		let queryString = `?per_page=${this.perPage}&page=${this.currentPage}${filters}`;

		if (this.resourceTypes.length) {
			this.resourceTypes.forEach((resourceType) => {
				queryString += `&resource-type[]=${resourceType}`;
			});
		}

		this.setQueryString();
		this.initialQueryString = ''; // Clear because we dont want to use it unless the page is refreshed.
		const url = `${this.endpoint}${queryString}`;

		try {
			const response = await fetch(url, { method: 'GET' });
			this.totalPages = await response.headers.get('x-wp-totalpages');
			this.totalPosts = await response.headers.get('x-wp-total');
			const json = await response.json();

			/**
			 * Output Content.
			 */
			if (json.length) {
				this.posts = json;
				this.renderContent();
				this.renderPagination();

				if (this.isInitialLoad) {
					const publicationsLoaded = new CustomEvent('emPublicationsLoaded');
					window.dispatchEvent(publicationsLoaded);
					this.isInitialLoad = false;
				}
			} else {
				/**
				 * Displays No Results found message.
				 */
				this.resultsContainer.setAttribute('data-loaded', 'true');
				this.resultsContainer.innerHTML = `
					<div class="em-resource-publications__item" style="margin-bottom:180px;">
						<div class="em-resource-publications__column" style="width:100%;">
							<h2>No Results Found.</h2>
						</div>
					</div>
				`;
				this.paginationContainer.innerHTML = '';
			}

			return true;
		} catch (error) {
			console.error('Error: ', error);
			return false;
		}
	}

	/**
	 * Sets variables for column display.
	 */
	checkColumns() {
		const { treatmentsSelect, conditionsSelect } = this;
		this.hasTreatment = treatmentsSelect !== null;
		this.hasCondition = conditionsSelect !== null;
		return true;
	}

	init() {
		/**
		 * Check if EmClient.restUrl is avaliable.
		 */
		const { EmClient } = this.window;
		if (!EmClient) {
			console.error('Error EmClient not found.');
			return false;
		}
		if (!EmClient.restUrl) {
			console.error('Error EmClient.restUrl not found.');
			return false;
		}

		this.initialQueryString = this.getQueryString();
		this.storageSetInitalData(this.filterKey);
		this.checkColumns();
		this.restUrl = EmClient.restUrl;
		this.nonce = EmClient.nonce;
		this.endpoint = `${this.restUrl}em-client/v1/resource/`;
		this.fetchPostData();
		this.resultsFilter();

		return true;
	}
}

document.addEventListener('DOMContentLoaded', () => {
	const publications = new ResourcePublications();
	if (publications.container) {
		publications.init();
	}
});
